import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { CeiTablePage, CeiTablePageRequest } from '@cei-ufg/ngx-arquitetura-cei-frontend';
import { ListRetrieveGrupo, ListRetrieveUsuario, Permissao } from '@cei-ufg/ngx-cei-seguranca';
import {
  CreatePessoaWithUsuario,
  ListRetrievePessoa,
  ListRetrievePessoaWithUsuario,
  ListRetrieveUsuarioComPessoa,
  PessoaCamposListagem,
  PessoaUsuarioResumo,
  PessoasApi,
  UpdatePessoaWithUsuario,
  UsuariosApi,
} from '../api';
import {
  paraRequisicaoPaginacaoOpenApi,
} from '../shared/admin-paginacao.utils';
import { formatCpfDisplay } from '../shared/pessoa-usuario-form/pessoa-usuario-form.utils';

/** Registro admin pessoa + usuário (resposta de com-usuario). */
export type PessoaComUsuario = ListRetrievePessoaWithUsuario;

export type AdminPessoaUsuarioRecord = PessoaComUsuario & {
  pessoaId: number;
  usuarioId?: number;
};

export interface PessoaUsuarioListItem extends PessoaComUsuario {
  pessoaId: number;
}

/** Payload de formulário — mapeado internamente para DTOs gerados. */
export interface PessoaUsuarioFormPayload {
  nome: string;
  cpf: string;
  usuario?: {
    login?: string;
    email?: string;
    password?: string;
    is_active?: boolean;
    is_staff?: boolean;
    user_permissions?: number[];
    groups?: number[];
  };
}

/** Linha da tabela admin (view model UI). */
export type PessoaListRow = {
  id: number;
  pessoaId: number | null;
  nome: string;
  cpf: string;
  login: string;
  email: string;
  usuario?: Pick<
    PessoaUsuarioResumo,
    'id' | 'login' | 'email' | 'is_active' | 'is_staff' | 'date_joined'
  >;
  pessoa?: PessoaCamposListagem | null;
};

/** Tamanho de página para carregar catálogos completos na união híbrida. */
const TAMANHO_CATALOGO_HIBRIDO = 1000;

/**
 * Adapter fino (RC28) entre telas de pessoa/usuário e clientes OpenAPI gerados.
 *
 * Delega HTTP a `PessoasApi` e `UsuariosApi`; mantém apenas mapeamento para UI.
 */
@Injectable({ providedIn: 'root' })
export class PessoaUsuarioApiService {
  private readonly pessoasApi = inject(PessoasApi);
  private readonly usuariosApi = inject(UsuariosApi);

  /** Catálogo unificado em memória (usuários + pessoas sem conta). */
  private catalogoHibrido$: Observable<PessoaListRow[]> | null = null;

  /**
   * Lista híbrida paginada: todas as contas + pessoas sem usuário.
   * Deduplica por pessoa_id quando usuário e pessoa estão vinculados.
   */
  listarPaginado(request: CeiTablePageRequest): Observable<CeiTablePage<PessoaListRow>> {
    const { page, pageSize } = paraRequisicaoPaginacaoOpenApi(request);

    return this.obterCatalogoHibrido().pipe(
      map((todas) => {
        const inicio = (page - 1) * pageSize;
        return {
          results: todas.slice(inicio, inicio + pageSize),
          count: todas.length,
        };
      }),
    );
  }

  /** Invalida o cache da listagem híbrida (após criar/editar pessoa). */
  invalidarCatalogoHibrido(): void {
    this.catalogoHibrido$ = null;
  }

  list(): Observable<{ results: PessoaUsuarioListItem[] }> {
    return this.pessoasApi.pessoaList().pipe(
      map((resposta) => ({
        results: (resposta.results ?? []).map(
          (pessoa) =>
            ({
              ...pessoa,
              pessoaId: pessoa.id,
            }) as PessoaUsuarioListItem,
        ),
      })),
    );
  }

  retrieveWithUsuario(id: number): Observable<PessoaComUsuario> {
    return this.pessoasApi.pessoaComUsuarioRetrieve(id);
  }

  resolveAdminRecord(id: number): Observable<AdminPessoaUsuarioRecord> {
    return this.retrieveWithUsuario(id).pipe(
      map((pessoa) => ({
        ...pessoa,
        pessoaId: pessoa.id,
        usuarioId: pessoa.usuario?.id,
      })),
    );
  }

  createWithUsuario(payload: PessoaUsuarioFormPayload): Observable<PessoaComUsuario> {
    return this.pessoasApi.pessoaComUsuarioCreate(this.paraCreateDto(payload)).pipe(
      tap(() => this.invalidarCatalogoHibrido()),
    );
  }

  updateWithUsuario(id: number, payload: PessoaUsuarioFormPayload): Observable<PessoaComUsuario> {
    return this.pessoasApi.pessoaComUsuarioUpdate(id, this.paraUpdateDto(payload)).pipe(
      tap(() => this.invalidarCatalogoHibrido()),
    );
  }

  uploadFoto(id: number, file: File): Observable<PessoaComUsuario> {
    return this.pessoasApi.pessoaFotoCreate(id, file) as Observable<PessoaComUsuario>;
  }

  deleteFoto(id: number): Observable<PessoaComUsuario> {
    return this.pessoasApi.pessoaFotoDestroy(id) as Observable<PessoaComUsuario>;
  }

  destroy(id: number): Observable<void> {
    return this.pessoasApi.pessoaDestroy(id).pipe(tap(() => this.invalidarCatalogoHibrido()));
  }

  /** Converte payload de formulário para DTO gerado de criação. */
  private paraCreateDto(payload: PessoaUsuarioFormPayload): CreatePessoaWithUsuario {
    return {
      id: '',
      nome: payload.nome,
      cpf: payload.cpf,
      usuario: payload.usuario,
    };
  }

  /** Converte payload de formulário para DTO gerado de atualização. */
  private paraUpdateDto(payload: PessoaUsuarioFormPayload): UpdatePessoaWithUsuario {
    return {
      id: 0,
      nome: payload.nome,
      cpf: payload.cpf,
      usuario: payload.usuario,
    };
  }

  private obterCatalogoHibrido(): Observable<PessoaListRow[]> {
    if (!this.catalogoHibrido$) {
      this.catalogoHibrido$ = forkJoin({
        usuarios: this.usuariosApi.usuarioComPessoaList(1, TAMANHO_CATALOGO_HIBRIDO),
        pessoas: this.pessoasApi.pessoaList(1, TAMANHO_CATALOGO_HIBRIDO),
      }).pipe(
        map(({ usuarios, pessoas }) =>
          this.unirCatalogos(usuarios.results ?? [], pessoas.results ?? []),
        ),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.catalogoHibrido$;
  }

  /**
   * Une contas e pessoas: 1 linha por pessoa vinculada + usuários órfãos + pessoas sem conta.
   */
  private unirCatalogos(
    usuarios: ListRetrieveUsuarioComPessoa[],
    pessoas: ListRetrievePessoa[],
  ): PessoaListRow[] {
    const porPessoaId = new Map<number, PessoaListRow>();
    const soUsuarios: PessoaListRow[] = [];

    for (const usuario of usuarios) {
      const linha = this.mapearLinhaUsuario(usuario);
      if (linha.pessoaId != null) {
        porPessoaId.set(linha.pessoaId, linha);
      } else {
        soUsuarios.push(linha);
      }
    }

    for (const pessoa of pessoas) {
      if (!porPessoaId.has(pessoa.id)) {
        porPessoaId.set(pessoa.id, this.mapearLinhaPessoa(pessoa));
      }
    }

    const uniao = [...porPessoaId.values(), ...soUsuarios];
    return uniao.sort((a, b) => {
      const nomeCmp = a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' });
      if (nomeCmp !== 0) {
        return nomeCmp;
      }
      return a.login.localeCompare(b.login, 'pt-BR', { sensitivity: 'base' });
    });
  }

  /** Pessoa (+ usuário opcional) → linha da tabela. */
  private mapearLinhaPessoa(pessoa: ListRetrievePessoa): PessoaListRow {
    const usuario = (pessoa.usuario ?? null) as PessoaUsuarioResumo | null;

    return {
      id: pessoa.id,
      pessoaId: pessoa.id,
      nome: pessoa.nome?.trim() ? pessoa.nome : '—',
      cpf: pessoa.cpf ? formatCpfDisplay(pessoa.cpf) : '—',
      login: usuario?.login?.trim() ? usuario.login : '—',
      email: usuario?.email?.trim() ? usuario.email : '—',
      pessoa: {
        id: pessoa.id,
        nome: pessoa.nome,
        cpf: pessoa.cpf,
        is_deleted: pessoa.is_deleted,
      },
      usuario: usuario
        ? {
            id: usuario.id,
            login: usuario.login,
            email: usuario.email,
            is_active: usuario.is_active,
            is_staff: usuario.is_staff,
            date_joined: usuario.date_joined,
          }
        : undefined,
    };
  }

  /** Conta (+ pessoa opcional) → linha da tabela. */
  private mapearLinhaUsuario(usuario: ListRetrieveUsuarioComPessoa): PessoaListRow {
    const pessoa = usuario.pessoa ?? null;

    return {
      id: usuario.id,
      pessoaId: pessoa?.id ?? null,
      nome: pessoa?.nome?.trim() ? pessoa.nome : '—',
      cpf: pessoa?.cpf ? formatCpfDisplay(pessoa.cpf) : '—',
      login: usuario.login,
      email: usuario.email,
      pessoa,
      usuario: {
        id: usuario.id,
        login: usuario.login,
        email: usuario.email,
        is_active: usuario.is_active,
        is_staff: usuario.is_staff,
        date_joined: usuario.date_joined,
      },
    };
  }
}

export type { ListRetrieveGrupo, ListRetrieveUsuario, Permissao, PessoaCamposListagem };
